import { ReactElement, Fragment, useState } from 'react';
import {
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from '@headlessui/react';
import {
  CheckIcon,
  ChevronUpDownIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';

export interface SelectProps<T> {
  options: T[];
  onUpdate?: (selectedIndex?: number, selected?: T) => void;
  label?: string;
  placeholder?: string;
  defaultSelectedIndex?: number;
  wrapperClassName?: string;
  labelClassName?: string;
  hideCloseIcon?: boolean;
  errorClass?: string;
  errorMessage?: string;
  disabled?: boolean;
}
// Select
export function Select<T extends { name: string }>({
  options,
  label,
  placeholder,
  onUpdate,
  defaultSelectedIndex,
  wrapperClassName,
  labelClassName,
  hideCloseIcon,
  errorMessage,
  errorClass,
  disabled,
}: SelectProps<T>): ReactElement<any> {
  const [selected, setSelected] = useState<
    (typeof options)[number] | undefined
  >(defaultSelectedIndex != null ? options[defaultSelectedIndex] : undefined);

  const hasErrors = !!errorMessage;
  const normalFocus = 'focus:border-br-primary focus:ring-br-primary';
  errorClass =
    errorClass !== undefined
      ? errorClass
      : 'border-2 border-rose-600 focus:border-rose-400 focus:ring-rose-200 ';
  const stateClassNames = hasErrors ? errorClass : normalFocus;

  return (
    <div className={`${wrapperClassName} flex items-center gap-x-2`}>
      <div className="flex-grow">
        <Listbox
          disabled={disabled}
          value={selected}
          onChange={(item) => {
            setSelected(item);
            onUpdate?.(item ? options.indexOf(item) : -1, item);
          }}
        >
          <Label
            className={`block text-sm font-medium text-gray-700 dark:text-gray-300 ${labelClassName}`}
          >
            {label}
          </Label>
          <div className="relative mt-1">
            <ListboxButton
              className={`relative w-full cursor-default rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 py-2 pr-10 pl-3 text-left shadow-sm focus:ring-1 focus:outline-none sm:text-sm ${stateClassNames}`}
            >
              {selected ? (
                <>
                  <span className="inline-flex w-full truncate">
                    <span className="truncate">{selected.name}</span>
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon
                      className="h-5 w-5 text-gray-400 dark:text-gray-500"
                      aria-hidden="true"
                    />
                  </span>
                </>
              ) : (
                <>
                  <span className="inline-flex w-full truncate">
                    <span className="truncate text-gray-400 dark:text-gray-500">
                      {placeholder}
                    </span>
                  </span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon
                      className="h-5 w-5 text-gray-400 dark:text-gray-500"
                      aria-hidden="true"
                    />
                  </span>
                </>
              )}
            </ListboxButton>

            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <ListboxOptions
                className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-900 py-1 text-base shadow-lg ring-1 ring-black/5 dark:ring-gray-600 focus:outline-none sm:text-sm"
                modal={false}
              >
                {options.map((option) => (
                  <ListboxOption
                    key={option.name}
                    className={({ active }) =>
                      `${
                        active
                          ? 'text-br-primary bg-br-primary/10'
                          : 'text-gray-900 dark:text-gray-100'
                      } relative cursor-default py-2 pr-9 pl-3 select-none`
                    }
                    value={option}
                  >
                    {({ selected }) => (
                      <>
                        <div className="flex">
                          <span
                            className={`${
                              selected ? 'font-semibold' : 'font-normal'
                            } ml-2 truncate`}
                          >
                            {option.name}
                          </span>
                        </div>

                        {selected ? (
                          <span className="text-br-primary absolute inset-y-0 right-0 flex items-center pr-4">
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </Transition>
          </div>
        </Listbox>
        {errorMessage && (
          <p className="mt-2 text-sm text-red-400">{errorMessage}</p>
        )}
      </div>
      <span
        className={`mt-[22px] ml-auto flex cursor-pointer items-center pr-2 ${
          selected ? '' : 'pointer-events-none opacity-0'
        }`}
        onClick={() => {
          setSelected(undefined);
          onUpdate?.(undefined, undefined);
        }}
      >
        {!hideCloseIcon && (
          <XCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" aria-hidden="true" />
        )}
      </span>
    </div>
  );
}
